import React, { useState, useRef, useEffect } from 'react';
import { FaPlay, FaPause, FaSpinner, FaBook, FaUser, FaUsers, FaStar, FaChevronRight, FaCog, FaGraduationCap, FaCheckCircle, FaVolumeUp, FaHeartbeat, FaMicroscope, FaBrain, FaDna } from 'react-icons/fa';

const NEETMedicalTutor = () => {
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [customTopic, setCustomTopic] = useState('');
  const [selectedProfessor, setSelectedProfessor] = useState('');
  const [examLevel, setExamLevel] = useState('UG'); // UG or PG
  const [isGenerating, setIsGenerating] = useState(false);
  const [videoGenerated, setVideoGenerated] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  const speechRef = useRef(null);
  const animationFrameRef = useRef(null);

  const neetUGSubjects = {
    biology: {
      name: 'Biology',
      icon: '🧬',
      color: '#10B981',
      topics: ['Cell Biology', 'Genetics & Evolution', 'Human Physiology', 'Plant Physiology', 'Ecology & Environment', 'Reproduction', 'Biotechnology', 'Molecular Biology']
    },
    physics: {
      name: 'Physics',
      icon: '⚛️',
      color: '#3B82F6',
      topics: ['Mechanics', 'Thermodynamics', 'Optics', 'Electromagnetism', 'Modern Physics', 'Waves & Oscillations', 'Current Electricity', 'Magnetism']
    },
    chemistry: {
      name: 'Chemistry',
      icon: '🧪',
      color: '#F59E0B',
      topics: ['Organic Chemistry', 'Physical Chemistry', 'Inorganic Chemistry', 'Biomolecules', 'Chemistry in Everyday Life', 'Chemical Bonding', 'Thermodynamics', 'Electrochemistry']
    },
    anatomy: {
      name: 'Human Anatomy',
      icon: '🫀',
      color: '#EF4444',
      topics: ['Cardiovascular System', 'Respiratory System', 'Nervous System', 'Digestive System', 'Skeletal System', 'Muscular System', 'Endocrine System', 'Urinary System']
    }
  };

  const neetPGSubjects = {
    medicine: {
      name: 'General Medicine',
      icon: '🏥',
      color: '#EF4444',
      topics: ['Cardiology', 'Gastroenterology', 'Nephrology', 'Endocrinology', 'Hematology', 'Infectious Diseases', 'Rheumatology', 'Respiratory Medicine']
    },
    surgery: {
      name: 'General Surgery',
      icon: '⚕️',
      color: '#8B5CF6',
      topics: ['GI Surgery', 'Trauma Surgery', 'Vascular Surgery', 'Endocrine Surgery', 'Breast Surgery', 'Surgical Oncology', 'Laparoscopic Surgery', 'Emergency Surgery']
    },
    obgyn: {
      name: 'Obstetrics & Gynecology',
      icon: '👶',
      color: '#EC4899',
      topics: ['High Risk Pregnancy', 'Infertility', 'Gynec Oncology', 'Menstrual Disorders', 'Labor & Delivery', 'Contraception', 'Endometriosis', 'Gynec Surgery']
    },
    pediatrics: {
      name: 'Pediatrics',
      icon: '🍼',
      color: '#14B8A6',
      topics: ['Neonatology', 'Growth & Development', 'Pediatric Infections', 'Pediatric Cardiology', 'Immunization', 'Genetic Disorders', 'Nutrition', 'Pediatric Emergency']
    },
    pathology: {
      name: 'Pathology',
      icon: '🔬',
      color: '#F97316',
      topics: ['Hematology', 'Clinical Pathology', 'Histopathology', 'Cytology', 'Microbiology', 'Immunology', 'Molecular Pathology', 'Autopsy']
    },
    pharmacology: {
      name: 'Pharmacology',
      icon: '💊',
      color: '#06B6D4',
      topics: ['CNS Drugs', 'Cardiovascular Drugs', 'Antimicrobials', 'Chemotherapy', 'Autonomic Drugs', 'Endocrine Drugs', 'GI Drugs', 'Drug Interactions']
    },
    radiology: {
      name: 'Radiology',
      icon: '📡',
      color: '#6366F1',
      topics: ['X-Ray Interpretation', 'CT Scan', 'MRI', 'Ultrasound', 'Interventional Radiology', 'Nuclear Medicine', 'Chest Imaging', 'Neuroradiology']
    },
    forensic: {
      name: 'Forensic Medicine',
      icon: '⚖️',
      color: '#64748B',
      topics: ['Autopsy', 'Toxicology', 'Medical Jurisprudence', 'Identification', 'Injuries', 'Sexual Offences', 'Death Certification', 'Forensic Psychiatry']
    }
  };

  const professors = [
    {
      id: 'male1',
      name: 'Dr. Rajesh Kumar',
      gender: 'Male',
      specialty: 'Medicine & Surgery',
      voice: 'male',
      avatarColor: '#EF4444',
      qualification: 'MD, DNB'
    },
    {
      id: 'female1',
      name: 'Dr. Priya Sharma',
      gender: 'Female',
      specialty: 'Anatomy & Physiology',
      voice: 'female',
      avatarColor: '#EC4899',
      qualification: 'MBBS, MD'
    },
    {
      id: 'male2',
      name: 'Prof. Arjun Mehta',
      gender: 'Male',
      specialty: 'Pathology & Microbiology',
      voice: 'male',
      avatarColor: '#8B5CF6',
      qualification: 'MD, DM'
    },
    {
      id: 'female2',
      name: 'Dr. Ananya Singh',
      gender: 'Female',
      specialty: 'Pediatrics & Gynecology',
      voice: 'female',
      avatarColor: '#14B8A6',
      qualification: 'MBBS, DNB'
    }
  ];

  const topicContent = {
    // NEET UG Topics
    'Cell Biology': 'Cell biology is the study of cell structure and function, fundamental to understanding all life processes. The cell is the basic unit of life, with prokaryotic cells lacking a nucleus and eukaryotic cells having membrane-bound organelles. Key organelles include the nucleus containing DNA, mitochondria for ATP production, ribosomes for protein synthesis, endoplasmic reticulum for protein and lipid processing, and Golgi apparatus for packaging. The cell membrane is selectively permeable, controlling substance entry and exit through diffusion, osmosis, and active transport. Cell division occurs through mitosis for growth and meiosis for gamete formation. Understanding cellular respiration, photosynthesis in plants, and cell signaling pathways is crucial for NEET preparation.',
    
    'Genetics & Evolution': 'Genetics studies heredity and variation in organisms. Mendel\'s laws form the foundation: law of segregation and independent assortment. DNA structure was discovered by Watson and Crick - a double helix with complementary base pairing. Gene expression involves transcription of DNA to mRNA and translation to proteins. Mutations can be point mutations or chromosomal aberrations. Genetic disorders include autosomal recessive like sickle cell anemia, autosomal dominant like Huntington\'s, and X-linked like hemophilia. Evolution theory by Darwin explains natural selection and survival of the fittest. Hardy-Weinberg equilibrium describes population genetics. Understanding inheritance patterns, pedigree analysis, and evolutionary mechanisms is essential for NEET.',
    
    'Human Physiology': 'Human physiology examines how body systems function. The cardiovascular system pumps blood through systemic and pulmonary circulation. Respiratory system exchanges gases via alveoli using ventilation and perfusion. Digestive system breaks down food through mechanical and chemical digestion, with enzyme secretion from salivary glands, stomach, pancreas, and intestines. Nervous system has central and peripheral divisions, with neurons transmitting impulses via synapses. Endocrine system secretes hormones regulating metabolism, growth, and reproduction. Excretory system filters blood through nephrons in kidneys. Understanding homeostasis, feedback mechanisms, and organ system integration is crucial for NEET.',
    
    'Cardiovascular System': 'The cardiovascular system consists of the heart, blood vessels, and blood. The heart has four chambers with valves preventing backflow. Cardiac cycle includes systole and diastole with electrical conduction through SA node, AV node, and Purkinje fibers. Blood pressure is systolic over diastolic, regulated by baroreceptors. Coronary arteries supply the heart muscle. Common pathologies include coronary artery disease, myocardial infarction, heart failure, arrhythmias, and valvular diseases. ECG interpretation shows heart electrical activity. Understanding cardiac anatomy, physiology, and common diseases is fundamental for medical students.',
    
    // NEET PG Topics
    'Cardiology': 'Cardiology deals with heart diseases and their management. Coronary artery disease results from atherosclerosis causing angina or myocardial infarction. Acute coronary syndrome management includes aspirin, clopidogrel, beta-blockers, ACE inhibitors, and revascularization. Heart failure has systolic and diastolic dysfunction, treated with diuretics, ACE inhibitors, and beta-blockers. Arrhythmias include atrial fibrillation requiring anticoagulation, ventricular tachycardia, and heart blocks. Valvular diseases include aortic stenosis, mitral regurgitation requiring valve replacement. Hypertension management follows JNC guidelines with lifestyle modification and antihypertensives. Understanding pathophysiology, clinical presentation, investigations including ECG, echocardiography, and management protocols is essential for PG exams.',
    
    'Gastroenterology': 'Gastroenterology studies digestive system diseases. Peptic ulcer disease is caused by H. pylori or NSAIDs, treated with PPIs and antibiotics. Inflammatory bowel disease includes Crohn\'s disease and ulcerative colitis with different presentations and treatments. Liver cirrhosis from chronic hepatitis or alcohol causes portal hypertension, ascites, and hepatic encephalopathy. Pancreatitis can be acute or chronic with different etiologies. GI bleeding needs urgent endoscopy for diagnosis and treatment. Hepatitis viruses A, B, C, D, E have different transmission and prognosis. Understanding endoscopy findings, liver function tests, and treatment protocols is crucial for gastroenterology.',
    
    'High Risk Pregnancy': 'High risk pregnancy requires special monitoring and management. Gestational diabetes affects glucose metabolism, requiring screening and insulin therapy. Preeclampsia presents with hypertension and proteinuria, potentially progressing to eclampsia requiring magnesium sulfate. Placenta previa and abruption cause antepartum hemorrhage needing immediate intervention. Multiple pregnancy has increased risks requiring frequent monitoring. Rh incompatibility needs anti-D immunoglobulin prophylaxis. Preterm labor may require tocolytics and corticosteroids for fetal lung maturity. IUGR needs growth monitoring and timely delivery. Understanding risk factors, screening protocols, and management strategies is essential for obstetrics.',
    
    'Neonatology': 'Neonatology focuses on newborn care, especially premature and sick infants. Birth asphyxia requires immediate resuscitation following NRP guidelines. Respiratory distress syndrome in premature babies needs surfactant therapy and respiratory support. Neonatal jaundice requires phototherapy or exchange transfusion based on bilirubin levels. Neonatal sepsis presents with subtle signs requiring blood cultures and antibiotics. Hypoglycemia in newborns needs glucose monitoring and correction. Congenital anomalies need immediate identification and referral. Understanding APGAR scoring, newborn resuscitation, and intensive care protocols is crucial for pediatrics.',
    
    'Hematology': 'Hematology studies blood disorders. Anemia classification includes microcytic like iron deficiency, macrocytic like B12 deficiency, and normocytic like acute blood loss. Leukemias are acute or chronic, myeloid or lymphoid with different treatment protocols. Lymphomas include Hodgkin and non-Hodgkin types requiring chemotherapy. Coagulation disorders include hemophilia, von Willebrand disease, and DIC. Thrombocytopenia has multiple causes requiring platelet transfusion if severe. Blood transfusion follows protocols checking compatibility. Understanding CBC interpretation, peripheral smear examination, and bone marrow findings is essential for pathology.',
    
    'CNS Drugs': 'CNS pharmacology covers drugs affecting brain and spinal cord. Antiepileptic drugs include phenytoin, carbamazepine, valproate with different mechanisms and side effects. Antidepressants are SSRIs, TCAs, MAOIs with varying efficacy and adverse effects. Antipsychotics include typical like haloperidol and atypical like risperidone for schizophrenia. Anxiolytics are benzodiazepines and buspirone for anxiety disorders. Opioids for pain management include morphine, fentanyl with addiction potential. Parkinson\'s disease drugs include levodopa, dopamine agonists. Understanding mechanism of action, clinical uses, adverse effects, and drug interactions is crucial for pharmacology.',
    
    'X-Ray Interpretation': 'X-ray interpretation requires systematic approach. Chest X-ray evaluation includes checking technical factors, cardiac size, lung fields, mediastinum, and bones. Pneumonia shows consolidation, pneumothorax shows absent lung markings, pleural effusion shows blunting of costophrenic angle. Cardiac enlargement indicates heart failure. Abdominal X-ray shows bowel gas pattern, air-fluid levels in obstruction, and free air under diaphragm in perforation. Bone X-rays show fractures, dislocations, and bone lesions. Understanding normal anatomy, common pathologies, and radiological signs is essential for radiology and clinical practice.',
    
    'Autopsy': 'Autopsy or post-mortem examination determines cause of death. External examination notes wounds, injuries, and body marks. Internal examination involves systematic organ dissection examining heart, lungs, brain, liver, kidneys. Samples collected for histopathology and toxicology. Cause of death classified as natural or unnatural. Medicolegal cases require police intimation. Time since death estimated using body temperature, rigor mortis, and decomposition changes. Understanding autopsy procedure, death certification, and medicolegal aspects is essential for forensic medicine practice and examinations.'
  };

  const generateContent = (topic) => {
    const predefined = topicContent[topic];
    if (predefined) return predefined;
    
    if (customTopic) {
      return `Let me explain ${customTopic} in detail for ${examLevel === 'UG' ? 'NEET UG' : 'NEET PG'}. This topic is crucial for your medical examination preparation. Understanding the pathophysiology, clinical presentation, diagnostic approach, and management protocols is essential. We'll cover the fundamental concepts, recent advances, and high-yield points frequently asked in exams. The key aspects include understanding the disease mechanism, recognizing clinical features, ordering appropriate investigations, and implementing evidence-based treatment. This comprehensive knowledge will help you excel in both theoretical examinations and practical clinical scenarios.`;
    }
    
    return `Today we'll explore ${topic}, an essential topic for ${examLevel === 'UG' ? 'NEET UG' : 'NEET PG'} preparation. This subject is crucial for understanding medical concepts and excelling in competitive examinations. We'll examine the core principles, clinical correlations, and examination-focused points. Understanding these concepts thoroughly will help you build a strong foundation for your medical career and achieve high scores in your examinations.`;
  };

  const simulateVideoGeneration = () => {
    setIsGenerating(true);
    setGenerationProgress(0);
    setVideoGenerated(false);

    const interval = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsGenerating(false);
          setVideoGenerated(true);
          const content = generateContent(selectedTopic || customTopic);
          setDuration(Math.ceil(content.split(' ').length * 0.4));
          return 100;
        }
        return prev + 2;
      });
    }, 50);
  };

  const handleGenerate = () => {
    if (!selectedProfessor || (!selectedTopic && !customTopic)) {
      alert('Please select a professor and topic');
      return;
    }
    simulateVideoGeneration();
  };

  const resetSelection = () => {
    setVideoGenerated(false);
    setGenerationProgress(0);
    setIsPlaying(false);
    setCurrentTime(0);
    setIsSpeaking(false);
    if (speechRef.current) {
      window.speechSynthesis.cancel();
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      const selectedProf = professors.find(p => p.id === selectedProfessor);
      
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(v => 
        selectedProf?.voice === 'female' 
          ? v.name.includes('Female') || v.name.includes('female') || v.name.includes('woman')
          : v.name.includes('Male') || v.name.includes('male') || v.name.includes('man')
      );
      
      if (preferredVoice) utterance.voice = preferredVoice;
      utterance.rate = 0.9;
      utterance.pitch = selectedProf?.voice === 'female' ? 1.1 : 0.9;
      
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => {
        setIsSpeaking(false);
        setIsPlaying(false);
        setCurrentTime(duration);
      };
      
      speechRef.current = utterance;
      window.speechSynthesis.speak(utterance);
      
      const startTime = Date.now();
      const animate = () => {
        if (window.speechSynthesis.speaking) {
          const elapsed = (Date.now() - startTime) / 1000;
          setCurrentTime(Math.min(elapsed, duration));
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      };
      animate();
    }
  };

  const togglePlayPause = () => {
    if (!isPlaying) {
      const content = generateContent(selectedTopic || customTopic);
      speakText(content);
      setIsPlaying(true);
    } else {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      setIsSpeaking(false);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
    }
    
    return () => {
      if (speechRef.current) {
        window.speechSynthesis.cancel();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const selectedProf = professors.find(p => p.id === selectedProfessor);
  const currentSubjects = examLevel === 'UG' ? neetUGSubjects : neetPGSubjects;

  return (
    <>
      <link 
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" 
        rel="stylesheet"
      />
      
      <style>{`
        body {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
          min-height: 100vh;
          color: #fff;
        }
        
        .glass-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
        }
        
        .exam-level-btn {
          padding: 10px 30px;
          border: 2px solid rgba(255, 255, 255, 0.2);
          background: rgba(255, 255, 255, 0.05);
          color: #fff;
          border-radius: 10px;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        
        .exam-level-btn:hover {
          background: rgba(255, 255, 255, 0.1);
        }
        
        .exam-level-btn.active {
          background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
          border-color: #EF4444;
          box-shadow: 0 5px 20px rgba(239, 68, 68, 0.5);
        }
        
        .subject-card {
          cursor: pointer;
          transition: all 0.3s ease;
          background: rgba(255, 255, 255, 0.05);
          border: 2px solid transparent;
          border-radius: 15px;
          min-height: 120px;
        }
        
        .subject-card:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateY(-5px);
        }
        
        .subject-card.active {
          border-color: #fff;
          box-shadow: 0 10px 30px rgba(239, 68, 68, 0.5);
        }
        
        .topic-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #fff;
          transition: all 0.3s ease;
          border-radius: 10px;
        }
        
        .topic-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #fff;
        }
        
        .topic-btn.active {
          background: linear-gradient(135deg, #7d005579 0%, #DC2626 100%);
          border-color: #EF4444;
        }
        
        .professor-card {
          cursor: pointer;
          transition: all 0.3s ease;
          background: rgba(255, 255, 255, 0.05);
          border: 2px solid transparent;
          border-radius: 15px;
        }
        
        .professor-card:hover {
          transform: translateY(-5px);
          background: rgba(255, 255, 255, 0.1);
        }
        
        .professor-card.active {
          background: linear-gradient(135deg, #02ab29ff 0%, #a528ffff 100%);
          border-color: #fff;
          box-shadow: 0 10px 30px rgba(239, 68, 68, 0.5);
        }
        
        .btn-generate {
          background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
          border: none;
          padding: 15px 30px;
          font-size: 1.1rem;
          font-weight: bold;
          border-radius: 10px;
          box-shadow: 0 10px 30px rgba(239, 68, 68, 0.5);
          transition: all 0.3s ease;
        }
        
        .btn-generate:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(239, 68, 68, 0.7);
        }
        
        .btn-generate:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .progress-bar-animated {
          background: linear-gradient(90deg, #EF4444 0%, #DC2626 50%, #EF4444 100%);
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
        }
        
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        
        .video-container {
          background: #000;
          border-radius: 15px;
          overflow: hidden;
          position: relative;
          aspect-ratio: 16/9;
        }
        
        .avatar-display {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          position: relative;
        }
        
        .avatar-circle {
          width: 200px;
          height: 200px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 100px;
          transition: all 0.3s ease;
        }
        
        .avatar-circle.speaking {
          animation: pulse 1s infinite;
          box-shadow: 0 0 40px rgba(239, 68, 68, 0.6);
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        .hospital-bg {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 40%;
          background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
          display: flex;
          align-items: flex-end;
          justify-content: center;
          padding: 20px;
        }
        
        .info-box {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
          padding: 15px;
        }
        
        .header-gradient {
          background: rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .text-gradient {
          background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        textarea {
          background: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          color: #fff !important;
          border-radius: 10px !important;
        }
        
        textarea:focus {
          background: rgba(255, 255, 255, 0.08) !important;
          border-color: #EF4444 !important;
          box-shadow: 0 0 0 0.2rem rgba(239, 68, 68, 0.25) !important;
          color: #fff !important;
        }
        
        textarea::placeholder {
          color: rgba(255, 255, 255, 0.4) !important;
        }
        
        .video-controls {
          background: rgba(0, 0, 0, 0.8);
          padding: 15px;
          border-radius: 10px;
        }
        
        .play-btn {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: linear-gradient(135deg, #30009eff 0%, #00af5bff 100%);
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .play-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 5px 20px rgba(142, 33, 33, 0.8);
        }
        
        .timeline {
          height: 6px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
          position: relative;
          cursor: pointer;
        }
        
        .timeline-progress {
          height: 100%;
          background: linear-gradient(90deg, #44ef47ff 0%, #008920ff 100%);
          border-radius: 3px;
          transition: width 0.1s linear;
        }
        
        .badge-custom {
          background: linear-gradient(135deg, #008428ff 0%, #007914ff 100%);
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.9rem;
        }
      `}</style>

      <div style={{ minHeight: '100vh', paddingBottom: '50px' }}>
        <div className="header-gradient py-5 my-5 mb-4">
          <div className="container">
            <div className="d-flex align-items-center justify-content-between flex-wrap">
              <div className="d-flex align-items-center gap-3">
                <FaHeartbeat className="text-success p-1" size={50} />
                <div>
                  <h3 className="mb-0  fw-bold text-white ">NEET Medical Tutor</h3>
                  <p className="mb-0 small text-white">AI-Powered Medical Educator</p>
                </div>
              </div>
              <div className="badge-custom">
                {examLevel === 'UG' ? 'NEET UG' : 'NEET PG'} Preparation
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          {!videoGenerated ? (
            <div className="row g-4">
              <div className="col-lg-6">
                {/* Exam Level Selection */}
                <div className="glass-card p-4 mb-4">
                  <h2 className="h5 mb-3 d-flex align-items-center gap-2">
                    <FaGraduationCap className="text-danger" />
                    Select Exam Level
                  </h2>
                  <div className="d-flex gap-3">
                    <button
                      onClick={() => {
                        setExamLevel('UG');
                        setSelectedSubject('');
                        setSelectedTopic('');
                        setCustomTopic('');
                      }}
                      className={`exam-level-btn flex-grow-1 ${examLevel === 'UG' ? 'active' : ''}`}
                    >
                      <div className="fw-bold mb-1">NEET UG</div>
                      <div className="small">Undergraduate Medical</div>
                    </button>
                    <button
                      onClick={() => {
                        setExamLevel('PG');
                        setSelectedSubject('');
                        setSelectedTopic('');
                        setCustomTopic('');
                      }}
                      className={`exam-level-btn flex-grow-1 ${examLevel === 'PG' ? 'active' : ''}`}
                    >
                      <div className="fw-bold mb-1">NEET PG</div>
                      <div className="small">Postgraduate Medical</div>
                    </button>
                  </div>
                </div>

                {/* Subject Selection */}
                <div className="glass-card p-4 mb-4">
                  <h2 className="h5 mb-3 d-flex align-items-center gap-2">
                    <FaBook className="text-danger" />
                    Select Subject
                  </h2>
                  <div className="row g-3">
                    {Object.entries(currentSubjects).map(([key, subject]) => (
                      <div key={key} className="col-6">
                        <div
                          onClick={() => {
                            setSelectedSubject(key);
                            setSelectedTopic('');
                            setCustomTopic('');
                          }}
                          className={`subject-card p-3 text-center ${selectedSubject === key ? 'active' : ''}`}
                          style={{ background: selectedSubject === key ? subject.color : 'rgba(255, 255, 255, 0.05)' }}
                        >
                          <div style={{ fontSize: '2.5rem' }} className="mb-2">{subject.icon}</div>
                          <div className="fw-semibold small">{subject.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Topic Selection */}
                {selectedSubject && (
                  <div className="glass-card p-4 mb-4">
                    <h2 className="h5 mb-3">Select Topic</h2>
                    <div className="d-grid gap-2 mb-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {currentSubjects[selectedSubject].topics.map((topic) => (
                        <button
                          key={topic}
                          onClick={() => {
                            setSelectedTopic(topic);
                            setCustomTopic('');
                          }}
                          className={`btn topic-btn text-start d-flex align-items-center justify-content-between ${
                            selectedTopic === topic ? 'active' : ''
                          }`}
                        >
                          <span>{topic}</span>
                          <FaChevronRight size={14} />
                        </button>
                      ))}
                    </div>
                    
                    <div className="pt-3 border-top border-secondary">
                      <label className="form-label text-danger small">Or enter custom topic:</label>
                      <textarea
                        value={customTopic}
                        onChange={(e) => {
                          setCustomTopic(e.target.value);
                          setSelectedTopic('');
                        }}
                        placeholder="Enter your specific medical topic to explain..."
                        className="form-control"
                        rows="3"
                      />
                    </div>
                  </div>
                )}

                {/* Professor Selection */}
                <div className="glass-card p-4 mb-4">
                  <h2 className="h5 mb-3 d-flex align-items-center gap-2">
                    <FaUsers className="text-danger" />
                    Select Medical Professor
                  </h2>
                  <div className="row g-3">
                    {professors.map((prof) => (
                      <div key={prof.id} className="col-6">
                        <div
                          onClick={() => setSelectedProfessor(prof.id)}
                          className={`professor-card p-3 ${selectedProfessor === prof.id ? 'active' : ''}`}
                        >
                          <div 
                            style={{ 
                              background: prof.avatarColor,
                              height: '100px',
                              borderRadius: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '3rem',
                              marginBottom: '10px'
                            }}
                          >
                            {prof.gender === 'Male' ? '👨‍⚕️' : '👩‍⚕️'}
                          </div>
                          <div className="fw-semibold small">{prof.name}</div>
                          <div className="text-danger" style={{ fontSize: '0.7rem' }}>{prof.qualification}</div>
                          <div className="text-muted" style={{ fontSize: '0.7rem' }}>{prof.specialty}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="btn btn-generate text-white w-100 d-flex align-items-center justify-content-center gap-2"
                >
                  {isGenerating ? (
                    <>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Generating Medical Lecture... {generationProgress}%
                    </>
                  ) : (
                    <>
                      <FaPlay />
                      Generate Medical Explanation
                    </>
                  )}
                </button>
              </div>

              {/* Right Panel - Preview/Info */}
              <div className="col-lg-6">
                <div className="glass-card p-4" style={{ minHeight: '500px' }}>
                  <h2 className="h5 mb-4">Medical Lecture Preview</h2>
                  
                  {isGenerating && (
                    <div>
                      <div className="info-box mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-danger small">Preparation Progress</span>
                          <span className="fw-bold">{generationProgress}%</span>
                        </div>
                        <div className="progress" style={{ height: '10px' }}>
                          <div 
                            className="progress-bar progress-bar-animated"
                            role="progressbar"
                            style={{ width: `${generationProgress}%` }}
                            aria-valuenow={generationProgress}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          />
                        </div>
                      </div>
                      
                      <div className="small text-danger">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Analyzing medical content...
                        </div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Preparing clinical explanations...
                        </div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Setting up medical professor avatar...
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Initializing voice synthesis...
                        </div>
                      </div>
                    </div>
                  )}

                  {!isGenerating && !selectedSubject && (
                    <div className="text-center py-5">
                      <FaHeartbeat size={64} className="text-danger mb-3" />
                      <h3 className="h5 mb-2">Welcome to NEET Medical Tutor</h3>
                      <p className="text-muted small mb-3">
                        AI-Powered medical education system for NEET UG & PG preparation
                      </p>
                      <div className="info-box text-start">
                        <div className="small">
                          <div className="fw-bold mb-2">Features:</div>
                          <div className="mb-1">🎯 Comprehensive medical topics</div>
                          <div className="mb-1">👨‍⚕️ Expert medical professors</div>
                          <div className="mb-1">🗣️ Clear voice explanations</div>
                          <div className="mb-1">📚 UG & PG level content</div>
                          <div>✅ High-yield exam points</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {!isGenerating && selectedSubject && (
                    <div>
                      <div className="alert alert-danger" role="alert">
                        <h3 className="h6 mb-2">Current Selection:</h3>
                        <div className="small">
                          <div className="mb-1"><strong>Exam Level:</strong> NEET {examLevel}</div>
                          <div className="mb-1"><strong>Subject:</strong> {currentSubjects[selectedSubject].name}</div>
                          <div className="mb-1"><strong>Topic:</strong> {selectedTopic || customTopic || 'Not selected'}</div>
                          <div><strong>Professor:</strong> {professors.find(p => p.id === selectedProfessor)?.name || 'Not selected'}</div>
                        </div>
                      </div>

                      <div className="info-box mb-3">
                        <h3 className="h6 mb-2 small">Lecture Details:</h3>
                        <ul className="small text-danger mb-0" style={{ paddingLeft: '20px' }}>
                          <li className="mb-1">✓ Detailed pathophysiology explanation</li>
                          <li className="mb-1">✓ Clinical presentation & diagnosis</li>
                          <li className="mb-1">✓ Investigation protocols</li>
                          <li className="mb-1">✓ Evidence-based management</li>
                          <li>✓ High-yield exam points</li>
                        </ul>
                      </div>

                      <div className="info-box" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                        <div className="small">
                          <div className="fw-bold text-danger mb-1">📌 Exam Focus:</div>
                          <div className="text-muted">
                            This topic is frequently asked in {examLevel === 'UG' ? 'NEET UG' : 'NEET PG'} examinations. 
                            The explanation covers all important concepts with clinical correlations.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div className="glass-card p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                    <div>
                      <h2 className="h4 mb-1">Medical Lecture Generated</h2>
                      <div className="small text-muted">
                        <span className="badge bg-danger me-2">NEET {examLevel}</span>
                        {currentSubjects[selectedSubject].name}
                      </div>
                    </div>
                    <button
                      onClick={resetSelection}
                      className="btn btn-outline-light"
                    >
                      Create New Lecture
                    </button>
                  </div>

                  <div className="video-container mb-3">
                    <div className="avatar-display">
                      <div 
                        className={`avatar-circle ${isSpeaking ? 'speaking' : ''}`}
                        style={{ background: selectedProf?.avatarColor }}
                      >
                        {selectedProf?.gender === 'Male' ? '👨‍⚕️' : '👩‍⚕️'}
                      </div>
                      
                      <div className="hospital-bg">
                        <div className="text-center">
                          <h3 className="h5 mb-0">{selectedProf?.name}</h3>
                          <p className="small text-danger mb-0">{selectedProf?.qualification}</p>
                          <p className="small text-muted mb-0">{selectedTopic || customTopic}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="video-controls">
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <button 
                        className="play-btn"
                        onClick={togglePlayPause}
                      >
                        {isPlaying ? <FaPause size={24} color="#fff" /> : <FaPlay size={24} color="#fff" />}
                      </button>
                      
                      <div className="flex-grow-1">
                        <div className="timeline mb-2">
                          <div 
                            className="timeline-progress"
                            style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                          />
                        </div>
                        <div className="d-flex justify-content-between small text-muted">
                          <span>{formatTime(currentTime)}</span>
                          <span>{formatTime(duration)}</span>
                        </div>
                      </div>
                      
                      {isSpeaking && (
                        <div className="d-flex align-items-center gap-2">
                          <FaVolumeUp className="text-danger" />
                          <span className="small text-danger">Lecturing...</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="row g-3 mt-3">
                    <div className="col-md-3">
                      <div className="info-box">
                        <div className="text-danger small mb-1">Exam Level</div>
                        <div className="fw-semibold">NEET {examLevel}</div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="info-box">
                        <div className="text-danger small mb-1">Subject</div>
                        <div className="fw-semibold">{currentSubjects[selectedSubject].name}</div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="info-box">
                        <div className="text-danger small mb-1">Topic</div>
                        <div className="fw-semibold small">{selectedTopic || customTopic}</div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="info-box">
                        <div className="text-danger small mb-1">Professor</div>
                        <div className="fw-semibold small">{selectedProf?.name}</div>
                      </div>
                    </div>
                  </div>

                  <div className="glass-card p-4 mt-4">
                    <h3 className="h6 mb-3 d-flex align-items-center gap-2">
                      <FaBook className="text-danger" />
                      Medical Lecture Transcript
                    </h3>
                    <div className="small text-muted" style={{ lineHeight: '1.8' }}>
                      {generateContent(selectedTopic || customTopic)}
                    </div>
                  </div>

                  <div className="row g-3 mt-3">
                    <div className="col-md-6">
                      <div className="info-box" style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <FaCheckCircle className="text-success" />
                          <div className="fw-bold small text-success">Key Learning Points</div>
                        </div>
                        <div className="small text-muted">
                          • Core medical concepts covered<br/>
                          • Clinical correlation included<br/>
                          • Exam-focused content<br/>
                          • Evidence-based approach
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-box" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <FaHeartbeat className="text-danger" />
                          <div className="fw-bold small text-danger">Clinical Relevance</div>
                        </div>
                        <div className="small text-muted">
                          • Real-world applications<br/>
                          • Diagnosis & management<br/>
                          • Patient care principles<br/>
                          • Latest guidelines followed
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NEETMedicalTutor;